/**
 * SINGLE SOURCE OF TRUTH FOR SUBSCRIPTION PLANS
 * 
 * This configuration is used by:
 * - Pricing page (LandingPage.jsx)
 * - Feature flags service (featureFlagsService.ts)
 * - Admin dashboards
 * - Tests
 * 
 * Any change here automatically propagates everywhere.
 */

export interface PlanFeature {
  text: string;
  included: boolean;
  flagKey?: string; // Maps to FeatureFlags key for consistency testing
}

export interface PlanConfig {
  id: string;
  name: string;
  subtitle: string;
  tier: 'retail' | 'professional' | 'institutional' | 'enterprise';
  priceMonthly: number | null; // null = custom pricing
  // Explicit yearly total in USD. Prefer this over yearlyDiscount so the
  // displayed price is exactly what Stripe charges (Stripe price IDs are
  // pinned to whole dollars, not a derived discount ratio). Falls back to
  // priceMonthly × 12 × yearlyDiscount when null.
  priceYearly?: number | null;
  yearlyDiscount: number; // legacy; only used when priceYearly is null
  description: string;
  features: PlanFeature[];
  badge: string | null;
  isEnterprise: boolean;
  analysesLimit: number | null; // null = unlimited
  showOnPricing?: boolean; // default true; set false to hide from pricing pages (kept for existing subscribers)
  // AI branding shown on the plan card: which flagship model this
  // tier gets by default, and the auto-fallback model when the plan's
  // monthly $ cap is hit. Both are labels for display only — the
  // actual routing is enforced by ai_plan_mapping in the backend.
  aiModelLabel?: string;         // e.g. 'GPT-5.5 · OpenAI flagship'
  aiFallbackLabel?: string;      // e.g. 'GPT-4o after monthly cap'
}

/**
 * Central plan configuration - SINGLE SOURCE OF TRUTH
 * Update here and all UI/logic will reflect changes
 */
export const PLAN_CONFIGS: PlanConfig[] = [
  {
    id: 'free',
    name: 'Free',
    subtitle: 'Start free',
    tier: 'retail',
    priceMonthly: 0,
    yearlyDiscount: 1,
    description: 'Get a taste of AI-graded discipline. No card required.',
    analysesLimit: 5,
    badge: 'Start here',
    isEnterprise: false,
    showOnPricing: true,
    aiModelLabel: 'GPT-5.5 · OpenAI flagship',
    aiFallbackLabel: 'GPT-4o after monthly cap',
    features: [
      { text: '5 AI analyses / month', included: true, flagKey: 'aiAnalysesLimit' },
      { text: 'TRADE / WAIT / NO-TRADE verdict', included: true, flagKey: 'canSeeBasicNoTrade' },
      { text: 'Basic trade journal', included: true, flagKey: 'canUseJournal' },
      { text: 'No counterfactual proof', included: false, flagKey: 'hasCounterfactualTracking' },
      { text: 'No discipline metrics (TDI)', included: false, flagKey: 'hasTdiScore' },
      { text: 'No Risk Officer / institutional engine', included: false, flagKey: 'hasInstitutionalReviewBlock' },
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    subtitle: 'Free forever',
    tier: 'retail',
    priceMonthly: 0,
    priceYearly: 0,
    yearlyDiscount: 1,
    description: 'All asset classes. Drag & drop any chart screenshot. Basic verdict.',
    analysesLimit: 5,
    badge: null,
    isEnterprise: false,
    showOnPricing: true,
    aiModelLabel: 'GPT-5.5 · OpenAI flagship',
    aiFallbackLabel: 'GPT-4o after monthly cap',
    features: [
      { text: '5 AI analyses / month', included: true, flagKey: 'aiAnalysesLimit' },
      { text: 'All asset classes', included: true },
      { text: 'Basic verdict', included: true, flagKey: 'canSeeBasicNoTrade' },
      { text: 'Multi-chart supported (within 5/mo)', included: true },
      { text: 'No counterfactual tracking', included: false, flagKey: 'hasCounterfactualTracking' },
      { text: 'No discipline metrics (TDI)', included: false, flagKey: 'hasTdiScore' },
    ],
  },
  {
    id: 'pro_trader',
    name: 'Pro Trader',
    subtitle: 'Built for traders',
    tier: 'professional',
    priceMonthly: 49,
    priceYearly: 290,
    yearlyDiscount: 0.493,
    description: 'Unlimited analyses + Counterfactual capital tracking + TDI + Multi-chart + Auto-saved journal.',
    analysesLimit: null,
    badge: 'Most Popular',
    isEnterprise: false,
    aiModelLabel: 'GPT-5.5 · OpenAI flagship',
    aiFallbackLabel: 'GPT-4o after monthly cap',
    features: [
      { text: 'Unlimited AI analyses', included: true, flagKey: 'aiAnalysesLimit' },
      { text: 'Counterfactual capital tracking', included: true, flagKey: 'hasCounterfactualTracking' },
      { text: 'Trader Discipline Index (TDI)', included: true, flagKey: 'hasTdiScore' },
      { text: 'Unlimited multi-chart review', included: true },
      { text: 'Auto-saved journal + history', included: true, flagKey: 'canUseJournal' },
      { text: 'Decision Quality Score', included: true, flagKey: 'hasDecisionQualityScore' },
      { text: 'Risk Avoidance Score', included: true, flagKey: 'hasRiskAvoidanceScore' },
      { text: '30-day free trial', included: true },
    ],
  },
  {
    id: 'institutional_ai',
    name: 'Institutional',
    subtitle: 'For Prop Firms',
    tier: 'institutional',
    priceMonthly: 149,
    priceYearly: 1490,
    yearlyDiscount: 0.833,
    description: 'Everything in Pro + Institutional Risk Officer mode + Impulse-trader prevention + Capital protection rules + Priority support.',
    analysesLimit: null,
    badge: 'Flagship',
    isEnterprise: false,
    aiModelLabel: 'Claude Fable 5 · Anthropic flagship',
    aiFallbackLabel: 'Claude Sonnet 5 after monthly cap',
    features: [
      { text: 'Everything in Pro Trader +', included: true },
      { text: 'Institutional Review Block (trade veto + discipline filters)', included: true, flagKey: 'hasInstitutionalReviewBlock' },
      { text: 'Risk Officer Stance', included: true, flagKey: 'hasRiskOfficerStance' },
      { text: 'Capital Protection Score', included: true, flagKey: 'hasCapitalProtectionScore' },
      { text: 'Market Alignment Score', included: true, flagKey: 'hasMarketAlignmentScore' },
      { text: 'Discipline Assessment', included: true, flagKey: 'hasDisciplineAssessment' },
      { text: 'Full Counterfactual Proof Engine', included: true, flagKey: 'hasFullCounterfactualProofEngine' },
      { text: 'Premium TDI Score (850 model)', included: true, flagKey: 'tdiVersion' },
      { text: 'TDI Score History Chart', included: true, flagKey: 'hasTdiHistoryChart' },
      { text: 'Behavioral Drift Detection', included: true, flagKey: 'hasBehavioralDriftDetection' },
      { text: 'Overtrading Cluster Analysis', included: true, flagKey: 'hasOvertradingClusterAnalysis' },
      { text: 'Institutional Audit Report (PDF)', included: true, flagKey: 'hasAuditPdf' },
      { text: 'PM-Ready Reporting', included: true, flagKey: 'hasPmReadyReporting' },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    subtitle: 'For Prop Firms, Hedge Funds & Institutions',
    tier: 'enterprise',
    priceMonthly: null,
    yearlyDiscount: 0.80,
    description: 'Built for high-volume teams requiring full governance, API access, and compliance modules.',
    analysesLimit: null,
    badge: null,
    isEnterprise: true,
    aiModelLabel: 'Claude Opus 4.8 · unlimited flagship',
    aiFallbackLabel: undefined,
    features: [
      { text: 'Everything in Institutional AI +', included: true },
      { text: 'Compliance Oversight Mode', included: true, flagKey: 'hasComplianceFeatures' },
      { text: 'Audit API', included: true, flagKey: 'hasAuditApi' },
      { text: 'Multi-user dashboards', included: true, flagKey: 'hasMultiUserDashboards' },
      { text: 'Daily/weekly discipline reports', included: true, flagKey: 'hasDisciplineReportsDailyWeekly' },
      { text: 'White-label AI (your firm branding)', included: true, flagKey: 'hasWhiteLabelAi' },
      { text: 'Custom Risk Officer profiles', included: true, flagKey: 'hasCustomRiskOfficerProfiles' },
      { text: 'SLA Priority Support', included: true, flagKey: 'hasPrioritySupport' },
      { text: 'Volume pricing', included: true },
    ],
  },
];

/**
 * Helper to get plan by ID
 */
export function getPlanById(planId: string): PlanConfig | undefined {
  return PLAN_CONFIGS.find(p => p.id === planId);
}

/**
 * Helper to get plan by name
 */
export function getPlanByName(name: string): PlanConfig | undefined {
  return PLAN_CONFIGS.find(p => p.name.toLowerCase() === name.toLowerCase());
}

/**
 * Get all plan IDs
 */
export function getAllPlanIds(): string[] {
  return PLAN_CONFIGS.map(p => p.id);
}

/**
 * Calculate yearly price from monthly
 */
export function calculateYearlyPrice(plan: PlanConfig): number | null {
  if (plan.priceMonthly === null) return null;
  // Match what Stripe actually charges: monthly × 10 (pay 10 months, get 12 free = ~17% off).
  // The Stripe price IDs in supabase/functions/create-checkout/index.ts are set up this way.
  // The plan.yearlyDiscount=0.80 field was the OLD intent (20% off) but reality is ~17%
  // (2 months free). UI must reflect Stripe to avoid surprise at checkout.
  return plan.priceMonthly * 10;
}

/**
 * Get features that require specific flags
 */
export function getFeaturesWithFlags(plan: PlanConfig): PlanFeature[] {
  return plan.features.filter(f => f.flagKey);
}

/**
 * Validate plan config against expected structure
 */
export function validatePlanConfig(plan: PlanConfig): string[] {
  const errors: string[] = [];
  
  if (!plan.id) errors.push('Plan must have an id');
  if (!plan.name) errors.push('Plan must have a name');
  if (!plan.description) errors.push('Plan must have a description');
  if (plan.features.length === 0) errors.push('Plan must have at least one feature');
  if (!plan.isEnterprise && plan.priceMonthly === null) {
    errors.push('Non-enterprise plans must have a monthly price');
  }
  
  return errors;
}

/**
 * Get DB-compatible plan data for seeding/comparison
 */
export function getPlanForDatabase(planId: string): {
  id: string;
  name: string;
  code: string;
  tier: string;
  price_usd_month: number | null;
  analyses_limit: number | null;
} | undefined {
  const plan = getPlanById(planId);
  if (!plan) return undefined;
  
  return {
    id: plan.id,
    name: plan.name,
    code: plan.id,
    tier: plan.tier,
    price_usd_month: plan.priceMonthly,
    analyses_limit: plan.analysesLimit,
  };
}

export default PLAN_CONFIGS;
